<script setup>
// Плаваючий загальний чат сайту. Це та сама «коробка», що й у
// TwitchChatWidget (перетягування, ресайз, прозорість, зум, фулскрін), але
// всередині — власна гілка коментарів (ChatBody): загальна на сайт, аніме
// чи серії. Вхід через Twitch або Discord.
// Оригінальний твіч-віджет лишається в репозиторії без використання.
const isOpen = useState('twitchChatOpen', () => false)
const { messages, unreadCount } = useTwitchChat()
const { isLive, viewerCount, streamTitle } = useTwitchStreamStatus()

// Гілка чату: загальна на сайт / на аніме / на конкретну серію
const {
  scopes,
  hasChoice,
  isChosen,
  chatConfig,
  selectScope,
  context: watchContext,
  requestPlayerChat
} = useSiteChat()

const box = ref(null)
const pos = ref({ x: 0, y: 0 })
const size = ref({ width: 350, height: 500 })
const opacity = ref(1)

const MIN_WIDTH = 150
const MIN_HEIGHT = 200

const OPACITY_STORAGE_KEY = 'site_chat_opacity'
// Масштаб навмисно не зберігаємо: після перезавантаження сторінки чат завжди
// починає зі 100%, бо зумлять його разово (щось прочитати), а не назавжди
const ZOOM_STORAGE_KEY = 'site_chat_zoom'

const zoom = ref(1)

// Розмір, який реально бачить користувач. Вікно position: fixed, тому все, що
// не влізло у вʼюпорт, вже нічим не дістати — сторінка від нього не росте і
// скрол униз не допоможе. Тому обрізаємо саме розмір, а не тільки координати.
// `size` лишається «бажаним»: звузили вікно браузера і повернули назад — чат
// повертає свій розмір.
const viewport = ref({ width: 0, height: 0 })

const readViewport = () => {
  if (import.meta.server) return
  viewport.value = { width: window.innerWidth, height: window.innerHeight }
}

const boxSize = computed(() => {
  if (!viewport.value.width) return size.value
  return {
    width: Math.min(size.value.width, viewport.value.width),
    height: Math.min(size.value.height, viewport.value.height)
  }
})

const clampToViewport = () => {
  if (import.meta.server) return
  readViewport()
  const maxX = window.innerWidth - boxSize.value.width
  const maxY = window.innerHeight - boxSize.value.height
  pos.value.x = Math.min(Math.max(pos.value.x, 0), Math.max(maxX, 0))
  pos.value.y = Math.min(Math.max(pos.value.y, 0), Math.max(maxY, 0))
}

// Пішли дивитися стрім — вважаємо чат Twitch прочитаним
const openStream = () => {
  unreadCount.value = 0
  bubbles.value = []
}

const openChat = () => {
  isOpen.value = true
}

const closeChat = () => {
  isOpen.value = false
}

// На сторінці перегляду чат відкриває плеєр — панеллю поруч з відео
const openChatHere = () => {
  if (watchContext.value?.slug) requestPlayerChat()
  else openChat()
}

// Стан можна перемкнути і ззовні (наприклад, перемикачем у налаштуваннях
// плеєра), тому позиціювання при першому відкритті прив'язане саме до
// isOpen, а не до конкретного місця виклику
watch(isOpen, (val) => {
  if (!val || !import.meta.client) return
  readViewport()
  if (pos.value.x === 0 && pos.value.y === 0) {
    pos.value = {
      x: window.innerWidth - boxSize.value.width - 24,
      y: window.innerHeight - boxSize.value.height - 96
    }
  }
  clampToViewport()
})

// --- Dragging ---
const dragging = ref(false)
let dragStart = { x: 0, y: 0, posX: 0, posY: 0 }

const onDragStart = (e) => {
  dragging.value = true
  const point = e.touches ? e.touches[0] : e
  dragStart = {
    x: point.clientX,
    y: point.clientY,
    posX: pos.value.x,
    posY: pos.value.y
  }
  window.addEventListener('mousemove', onDragMove)
  window.addEventListener('mouseup', onDragEnd)
  window.addEventListener('touchmove', onDragMove, { passive: false })
  window.addEventListener('touchend', onDragEnd)
}

const onDragMove = (e) => {
  if (!dragging.value) return
  e.preventDefault()
  const point = e.touches ? e.touches[0] : e
  const dx = point.clientX - dragStart.x
  const dy = point.clientY - dragStart.y
  pos.value.x = dragStart.posX + dx
  pos.value.y = dragStart.posY + dy
  clampToViewport()
}

const onDragEnd = () => {
  dragging.value = false
  window.removeEventListener('mousemove', onDragMove)
  window.removeEventListener('mouseup', onDragEnd)
  window.removeEventListener('touchmove', onDragMove)
  window.removeEventListener('touchend', onDragEnd)
}

// --- Resizing ---
const resizing = ref(false)
let resizeStart = { x: 0, y: 0, width: 0, height: 0 }

const onResizeStart = (e) => {
  resizing.value = true
  const point = e.touches ? e.touches[0] : e
  resizeStart = {
    x: point.clientX,
    y: point.clientY,
    width: size.value.width,
    height: size.value.height
  }
  window.addEventListener('mousemove', onResizeMove)
  window.addEventListener('mouseup', onResizeEnd)
  window.addEventListener('touchmove', onResizeMove, { passive: false })
  window.addEventListener('touchend', onResizeEnd)
}

const onResizeMove = (e) => {
  if (!resizing.value) return
  e.preventDefault()
  const point = e.touches ? e.touches[0] : e
  const dx = point.clientX - resizeStart.x
  const dy = point.clientY - resizeStart.y
  // Тягнути більше за екран нема сенсу — нижній край з полем вводу просто
  // поїде за межі вʼюпорту і стане недосяжним
  size.value.width = Math.min(
    Math.max(MIN_WIDTH, resizeStart.width + dx),
    window.innerWidth
  )
  size.value.height = Math.min(
    Math.max(MIN_HEIGHT, resizeStart.height + dy),
    window.innerHeight
  )
  clampToViewport()
}

const onResizeEnd = () => {
  resizing.value = false
  window.removeEventListener('mousemove', onResizeMove)
  window.removeEventListener('mouseup', onResizeEnd)
  window.removeEventListener('touchmove', onResizeMove)
  window.removeEventListener('touchend', onResizeEnd)
}

// --- Opacity ---
const onOpacityChange = (value) => {
  opacity.value = value
  if (import.meta.client) {
    try {
      localStorage.setItem(OPACITY_STORAGE_KEY, String(opacity.value))
    } catch (e) {}
  }
}

// --- Zoom (transform: scale на вмісті чату) ---
// Кнопками керує ChatToolbar, тут лишається саме значення. Живе тільки в межах
// поточної сторінки — у localStorage не їде

// --- Гілка чату ---
// Саму стрічку малює ChatBody, тут лишається тільки вибір гілки: яку показати
// (page-id, заголовок, адресу віддає useSiteChat)

// Питання «який чат відкрити?» ставимо лише в момент відкриття вікна: якщо
// користувач перейде на сторінку аніме з уже відкритим чатом, гілку він
// перемкне перемикачем зверху, а не діалогом
const askScope = ref(false)

watch(isOpen, (val) => {
  if (!val) return
  // Перше відкриття на сторінці аніме — спершу питаємо гілку
  askScope.value = hasChoice.value && !isChosen.value
})

// Пішли зі сторінки перегляду, поки висіло питання — обирати вже нема з чого
watch(hasChoice, (val) => {
  if (!val) askScope.value = false
})

const chooseScope = (id) => {
  selectScope(id)
  askScope.value = false
}

// --- Сповіщення (бабли) ---
const {
  settings: notifySettings,
  bubbles,
  unread,
  chatVisible,
  loadSettings: loadNotifySettings,
  hideBubble,
  clearBubbles,
  pushBubble,
  startFeed,
  stopFeed
} = useChatNotifications()

// У фулскріні кнопка чату живе за тими самими правилами, що й керування
// плеєра: пішла мишка — пішли обидва
const playerControlsVisible = useState('playerControlsVisible', () => false)

// Клік по баблу коментаря веде в той самий чат, звідки він прийшов
const openBubbleSource = async (bubble) => {
  hideBubble(bubble.key)
  if (bubble.source === 'twitch') return

  // Бабл живе хвилину, за цей час можна встигнути піти на іншу сторінку —
  // тоді спершу повертаємось туди, де коментар лишили
  const target = bubble.target
  if (target?.slug) {
    const path = `/watch/${target.slug}`
    const ep =
      target.episodeIndex === undefined || target.episodeIndex === null
        ? null
        : String(target.episodeIndex)
    const sameEpisode = ep === null || String(route.query.ep ?? '0') === ep

    if (route.path !== path || !sameEpisode) {
      await navigateTo({ path, query: ep === null ? {} : { ep } })
    }
  }

  selectScope(bubble.source)
  askScope.value = false
  isOpen.value = true
}

// --- Прихід за посиланням від бота ---
// Бот присилає посилання на конкретний коментар виду
// https://anime.olsior.tv/#_A1RnVpV/58 — це формат самого comments.app
// (тред/номер), і його ж віджет читає з хеша, щоб підсвітити відповідь.
// Людина йде по такому посиланню саме читати відповідь, тож чат відкриваємо
// одразу, не чекаючи кліку по кнопці.
const route = useRoute()
const COMMENT_HASH_RE = /^#([A-Za-z0-9\-_]{8,16})\/\d+$/

const hasCommentHash = () => {
  try {
    return COMMENT_HASH_RE.test(decodeURIComponent(window.location.hash))
  } catch (e) {
    return false
  }
}

onMounted(() => {
  // На сторінках перегляду коментар з хеша належить блокам під плеєром —
  // їхні віджети підхоплять його самі, плаваючий чат тут ні до чого
  if (hasCommentHash() && !route.path.startsWith('/watch/')) {
    openChat()
  }
})

// --- Fullscreen: слідкуємо за нативним fullscreen-елементом (плеєр і т.д.),
// щоб віджет чату телепортувався всередину нього — інакше він ховається
// за межами top-layer під час фулскріна
const fullscreenTarget = ref('body')

const getFullscreenElement = () =>
  document.fullscreenElement ||
  document.webkitFullscreenElement ||
  document.mozFullScreenElement ||
  document.msFullscreenElement ||
  null

const handleFullscreenChange = () => {
  fullscreenTarget.value = getFullscreenElement() || 'body'
}

// У фулскріні кнопку-тогл ховаємо, щоб не заважала елементам керування
// плеєра — саме вікно чату (якщо вже відкрите) лишається видимим
const isFullscreenActive = computed(() => fullscreenTarget.value !== 'body')

// --- Бабли з новими повідомленнями ---
// Twitch приходить пушем сюди, коментарі сайту — опитуванням у
// useChatNotifications. Показуємо і те, й те одним списком з підписом джерела.
if (import.meta.client) {
  watch(messages, (val) => {
    if (!notifySettings.value.enabled || !notifySettings.value.twitch) return
    if (!isLive.value || !val.length) return
    const msg = val[val.length - 1]
    pushBubble({
      source: 'twitch',
      id: msg.id,
      author: msg.author,
      color: msg.color,
      text: msg.text
    })
  })

  // Стрім скінчився — прибираємо те, що лишилося висіти від Twitch
  watch(isLive, (val) => {
    if (!val) clearBubbles('twitch')
  })
}

const FS_EVENTS = [
  'fullscreenchange',
  'webkitfullscreenchange',
  'mozfullscreenchange',
  'MSFullscreenChange'
]

onMounted(() => {
  readViewport()
  window.addEventListener('resize', clampToViewport)
  // На мобільних висота вʼюпорту змінюється без події resize — коли ховається
  // адресний рядок або вилазить клавіатура
  window.visualViewport?.addEventListener('resize', clampToViewport)
  window.addEventListener('orientationchange', clampToViewport)
  FS_EVENTS.forEach((evt) =>
    document.addEventListener(evt, handleFullscreenChange)
  )

  try {
    const savedOpacity = localStorage.getItem(OPACITY_STORAGE_KEY)
    if (savedOpacity !== null) opacity.value = Number(savedOpacity)

    // Прибираємо масштаб, збережений старою версією віджета, — інакше він
    // тягнувся б у нові сесії
    localStorage.removeItem(ZOOM_STORAGE_KEY)
  } catch (e) {}

  // Віджет висить у app.vue на всіх сторінках, тож опитування гілок живе тут
  loadNotifySettings()
  startFeed()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', clampToViewport)
  window.visualViewport?.removeEventListener('resize', clampToViewport)
  window.removeEventListener('orientationchange', clampToViewport)
  FS_EVENTS.forEach((evt) =>
    document.removeEventListener(evt, handleFullscreenChange)
  )
  onDragEnd()
  onResizeEnd()
  stopFeed()
})

// --- Прев'ю стріму можна приховати вручну (не чіпаючи кнопку чату) ---
const previewHidden = ref(false)

watch(isLive, (val) => {
  // Коли стрім знову офлайн - скидаємо, щоб наступного разу, коли він
  // стане онлайн, прев'ю знову з'явилося за замовчуванням
  if (!val) previewHidden.value = false
})

const hidePreview = () => {
  previewHidden.value = true
}
</script>

<template>
  <ClientOnly>
    <Teleport :to="fullscreenTarget">
      <!--
        Нижній правий кут ділять дві незалежні речі: тост про стрім (це суто
        Twitch — лічильник, бабли і перехід на канал) і кругла кнопка, яка
        відкриває загальний чат сайту. Тому вони в одному ряду, а не
        підміняють одна одну.
      -->
      <div
        v-if="!isFullscreenActive"
        class="right-[10px] lg:right-4 bottom-[10px] lg:bottom-4 z-[2147483000] fixed flex items-center gap-[10px]"
      >
        <!-- Тост "стрім онлайн" — веде на канал -->
        <a
          v-if="isLive && !previewHidden"
          href="https://www.twitch.tv/olsior"
          target="_blank"
          rel="noopener noreferrer"
          @click="openStream"
          class="flex items-center gap-[10px] bg-error hover:bg-error/90 shadow-lg px-3 py-2 rounded-lg w-60 text-error-content"
          title="Дивитися стрім на Twitch"
        >
          <span class="relative flex w-3 h-3 shrink-0">
            <span
              class="inline-flex absolute bg-error-content opacity-75 rounded-full w-full h-full animate-ping"
            ></span>
            <span
              class="inline-flex relative bg-error-content rounded-full w-3 h-3"
            ></span>
          </span>

          <div class="flex-1 min-w-0">
            <p class="font-semibold text-xs truncate">Стрім онлайн</p>
            <p class="opacity-80 text-[10px] truncate">{{ streamTitle }}</p>
          </div>

          <!-- Непрочитані рахуються по чату Twitch, тому лічильник живе тут -->
          <span
            v-if="unreadCount > 0"
            class="flex justify-center items-center bg-base-100 px-1 rounded-full min-w-[18px] h-[18px] text-[10px] text-error shrink-0"
          >
            {{ unreadCount > 99 ? '99+' : unreadCount }}
          </span>
        </a>

        <!-- Кнопка загального чату сайту -->
        <button
          v-if="!isOpen"
          @click="openChat"
          class="relative flex flex-shrink-0 justify-center items-center bg-primary hover:bg-primary-focus shadow-lg rounded-full w-14 h-14 text-primary-content btn btn-circle"
          title="Відкрити чат"
        >
          <!-- Непрочитані з усіх чатів, за якими стежимо -->
          <span
            v-if="unread > 0"
            class="-top-0.5 -right-0.5 z-10 absolute flex justify-center items-center bg-error px-1 rounded-full min-w-[20px] h-[20px] font-medium text-[11px] text-error-content"
          >
            {{ unread > 99 ? '99+' : unread }}
          </span>

        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
        >
          <!-- Icon from TDesign Icons by TDesign - https://github.com/Tencent/tdesign-icons/blob/main/LICENSE -->
          <path
            fill="currentColor"
            d="M8.796 17.027H8.75c-1.153 0-2.254-.188-3.262-.53L2.65 17.92l.352-2.712C1.162 13.855 0 11.861 0 9.64c0-4.083 3.918-7.39 8.75-7.39c4.174 0 7.665 2.468 8.54 5.77a9 9 0 0 0-.6-.02c-4.364 0-8.19 3.037-8.19 7.11c0 .67.104 1.312.296 1.917M6 8a1 1 0 1 0 0-2a1 1 0 0 0 0 2m5.5.007a1 1 0 1 0 0-2a1 1 0 0 0 0 2"
          />
          <path
            fill="currentColor"
            d="M21.874 19.52C23.187 18.405 24 16.863 24 15.16C24 11.758 20.754 9 16.75 9S9.5 11.758 9.5 15.161s3.246 6.161 7.25 6.161c.95 0 1.856-.155 2.686-.437l2.438 1.407zm-7.564-5.362a1 1 0 1 1 0-2a1 1 0 0 1 0 2m4.88 0a1 1 0 1 1 0-2a1 1 0 0 1 0 2"
          />
        </svg>
        </button>
      </div>

      <!--
        Бабли з новими повідомленнями — і Twitch, і чати сайту. Поки чат
        відкритий вони не потрібні: те саме буде видно в самому чаті.
      -->
      <div
        v-if="bubbles.length && !chatVisible && !isFullscreenActive"
        class="right-4 bottom-20 z-[2147483000] fixed flex flex-col items-end gap-2 w-72 max-w-[calc(100vw-2rem)] pointer-events-none"
      >
        <TransitionGroup name="bubble">
          <ChatBubble
            v-for="b in bubbles"
            :key="b.key"
            :bubble="b"
            @open="openBubbleSource(b)"
            @dismiss="hideBubble(b.key)"
          />
        </TransitionGroup>
      </div>

      <!--
        Фулскрін: кнопка з лічильником непрочитаних і бабли — праворуч зверху,
        щоб не лізти на елементи керування внизу.
      -->
      <div
        v-if="isFullscreenActive && !chatVisible"
        class="top-4 right-4 z-[2147483000] fixed flex flex-col items-end gap-2 w-72 max-w-[calc(100vw-2rem)] pointer-events-none"
      >
        <Transition name="bubble">
          <button
            v-if="playerControlsVisible"
            @click="openChatHere"
            class="relative flex flex-shrink-0 justify-center items-center bg-primary hover:bg-primary-focus shadow-lg rounded-full w-12 h-12 text-primary-content transition-colors pointer-events-auto"
            title="Відкрити чат"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
            >
              <path
                fill="currentColor"
                d="M8.796 17.027H8.75c-1.153 0-2.254-.188-3.262-.53L2.65 17.92l.352-2.712C1.162 13.855 0 11.861 0 9.64c0-4.083 3.918-7.39 8.75-7.39c4.174 0 7.665 2.468 8.54 5.77a9 9 0 0 0-.6-.02c-4.364 0-8.19 3.037-8.19 7.11c0 .67.104 1.312.296 1.917M6 8a1 1 0 1 0 0-2a1 1 0 0 0 0 2m5.5.007a1 1 0 1 0 0-2a1 1 0 0 0 0 2"
              />
              <path
                fill="currentColor"
                d="M21.874 19.52C23.187 18.405 24 16.863 24 15.16C24 11.758 20.754 9 16.75 9S9.5 11.758 9.5 15.161s3.246 6.161 7.25 6.161c.95 0 1.856-.155 2.686-.437l2.438 1.407zm-7.564-5.362a1 1 0 1 1 0-2a1 1 0 0 1 0 2m4.88 0a1 1 0 1 1 0-2a1 1 0 0 1 0 2"
              />
            </svg>

            <span
              v-if="unread > 0"
              class="-top-1 -right-1 absolute flex justify-center items-center bg-error px-1 rounded-full min-w-[18px] h-[18px] text-[10px] text-error-content"
            >
              {{ unread > 99 ? '99+' : unread }}
            </span>
          </button>
        </Transition>

        <TransitionGroup name="bubble">
          <ChatBubble
            v-for="b in bubbles"
            :key="b.key"
            :bubble="b"
            @open="openBubbleSource(b)"
            @dismiss="hideBubble(b.key)"
          />
        </TransitionGroup>
      </div>

      <!-- Floating chat window -->
      <div
        v-if="isOpen"
        ref="box"
        class="z-[2147483000] fixed flex flex-col shadow-2xl border border-base-content/10 rounded-lg overflow-hidden select-none"
        :style="{
          left: pos.x + 'px',
          top: pos.y + 'px',
          width: boxSize.width + 'px',
          height: boxSize.height + 'px',
          opacity: opacity
        }"
      >
        <!--
          Панель тримаємо ПОЗА фреймом, а не поверх нього: Twitch навмисно
          вимикає ввід/модерацію в чаті, якщо якийсь елемент перекриває
          iframe (захист від clickjacking по кнопках бан/тайм-аут) —
          через це власник/модератори каналу не могли писати в чат.
        -->
        <ChatToolbar
          v-model:zoom="zoom"
          :opacity="opacity"
          show-opacity
          draggable
          @update:opacity="onOpacityChange"
          @close="closeChat"
          @mousedown="onDragStart"
          @touchstart="onDragStart"
        />

        <!-- Перше відкриття на сторінці аніме: питаємо, який чат показати -->
        <div
          v-if="askScope"
          class="flex flex-col flex-1 justify-center gap-2 bg-base-300 p-4 overflow-y-auto"
          @mousedown.stop
          @touchstart.stop
        >
          <p class="mb-1 font-semibold text-sm text-center">
            Який чат відкрити?
          </p>
          <button
            v-for="s in scopes"
            :key="s.id"
            @click="chooseScope(s.id)"
            class="bg-base-200 hover:bg-primary hover:text-primary-content px-3 py-2 rounded-lg text-left transition-colors"
          >
            <span class="block font-medium text-sm">{{ s.label }}</span>
            <span class="block opacity-60 text-[11px] truncate">{{
              s.hint
            }}</span>
          </button>
          <p class="opacity-50 mt-1 text-[10px] text-center">
            Потім чат можна перемкнути зверху
          </p>
        </div>

        <!--
          Зум і блокування вводу під час перетягування — на контейнері, щоб
          стрічка не ловила мишу, поки вікно тягнуть
        -->
        <div
          v-if="!askScope"
          class="relative flex-1 bg-base-300 overflow-hidden"
        >
          <div
            class="flex w-full h-full"
            :style="
              zoom === 1
                ? { pointerEvents: dragging || resizing ? 'none' : 'auto' }
                : {
                    width: 100 / zoom + '%',
                    height: 100 / zoom + '%',
                    transform: `scale(${zoom})`,
                    transformOrigin: 'top left',
                    pointerEvents: dragging || resizing ? 'none' : 'auto'
                  }
            "
          >
            <ChatBody
              :page-id="chatConfig.pageId"
              :page-title="chatConfig.pageTitle"
              :page-url="chatConfig.pageUrl"
            />
          </div>
        </div>

        <!-- Ручка ресайзу — окремою смугою знизу, а не поверх фрейму -->
        <div
          class="bottom-0 left-0 absolute flex flex-shrink-0 justify-end items-center px-1 w-full h-3 cursor-nwse-resize"
          @mousedown="onResizeStart"
          @touchstart="onResizeStart"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            class="opacity-40"
          >
            <path
              fill="currentColor"
              d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM18 18H16V16H18V18ZM14 22H12V20H14V22Z"
            />
          </svg>
        </div>
      </div>
    </Teleport>
  </ClientOnly>
</template>

<style scoped>
.bubble-enter-active,
.bubble-leave-active {
  transition: all 0.25s ease;
}
.bubble-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.bubble-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
</style>
